import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  User, Sliders, Cloud, Send, Pencil, Check, Info, Zap, ShieldCheck,
  LogOut, CheckCircle2, Circle, ChevronRight, DollarSign, Gift, Unlink, AlertTriangle, Eye, EyeOff, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiGet, apiPost, apiFetch, safeJson } from "@/lib/api";
import { STRATEGY_INFO } from "@/constants/strategies";
import { LoadingBlock, Spinner } from "@/components/Spinner";
import Modal from "@/components/Modal";
import TelegramGuide from "@/components/TelegramGuide";
import { CLOUDBET_REFERRAL_URL } from "@/constants/links";

function Switch({ on, onToggle, testId }) {
  return (
    <button onClick={onToggle} data-testid={testId}
      className={`w-12 h-7 rounded-full p-0.5 transition-colors ${on ? "bg-[#10b981]" : "bg-[#334155]"}`}>
      <span className={`block w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function Settings({ onOpenAdmin, onLogout, onStrategyToggle }) {
  const { isAdmin } = useAuth();
  const [subTab, setSubTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // edit profile modal
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);

  // api key modal
  const [keyModal, setKeyModal] = useState(null); // 'cloudbet' | 'telegram'
  const [keyValue, setKeyValue] = useState("");
  const [botTokenValue, setBotTokenValue] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [testingTg, setTestingTg] = useState(false);
  const [testingCb, setTestingCb] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // strategy info / amount modal
  const [infoStrat, setInfoStrat] = useState(null);
  const [expandedStrat, setExpandedStrat] = useState(null);
  const [amountStrat, setAmountStrat] = useState(null);
  const [amountVal, setAmountVal] = useState("");
  const [dcaAmountStrat, setDcaAmountStrat] = useState(null);
  const [dcaAmountVal, setDcaAmountVal] = useState("");
  const [dcaAmountField, setDcaAmountField] = useState("dca_amount");

  const load = useCallback(async () => {
    const data = await apiGet("/api/user/profile");
    if (data) setProfile(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStrategyLocal = (name, patch) =>
    setProfile((p) => ({ ...p, strategies: { ...p.strategies, [name]: { ...p.strategies[name], ...patch } } }));

  const toggleStrategy = async (name, enabled) => {
    updateStrategyLocal(name, { enabled });
    const res = await apiFetch(`/api/user/profile/toggle-strategy?strategy_name=${encodeURIComponent(name)}&enabled=${enabled}`, { method: "POST" });
    const data = await safeJson(res);
    if (!data || !data.success) { updateStrategyLocal(name, { enabled: !enabled }); toast.error("Failed to update strategy"); }
    else if (onStrategyToggle) onStrategyToggle();  // refresh conditional nav tabs (Set Handicap / Match Winner) immediately
  };

  const saveAmount = async () => {
    const amt = parseFloat(amountVal);
    if (isNaN(amt) || amt < 1) { toast.error("Minimum trade amount is $1.00"); return; }
    const name = amountStrat;
    updateStrategyLocal(name, { trade_amount: amt });
    setAmountStrat(null);
    const res = await apiFetch(`/api/user/profile/strategy-amount?strategy_name=${encodeURIComponent(name)}&amount=${amt}`, { method: "POST" });
    const data = await safeJson(res);
    if (!data || !data.success) { toast.error("Failed to update amount"); load(); }
    else toast.success("Trade amount updated");
  };

  // Persist arbitrary per-strategy fields (used for Match Winner DCA settings).
  const saveStrategyPatch = async (name, patch) => {
    const res = await apiFetch("/api/user/profile/strategies", {
      method: "POST", body: JSON.stringify({ strategies: { [name]: patch } }),
    });
    const data = await safeJson(res);
    return !!(data && data.success);
  };

  const toggleDca = async (name, on) => {
    updateStrategyLocal(name, { dca_enabled: on });
    const ok = await saveStrategyPatch(name, { dca_enabled: on });
    if (!ok) { updateStrategyLocal(name, { dca_enabled: !on }); toast.error("Failed to update DCA"); }
  };

  const saveDcaAmount = async () => {
    const amt = parseFloat(dcaAmountVal);
    if (isNaN(amt) || amt < 1) { toast.error("Minimum DCA amount is $1.00"); return; }
    const name = dcaAmountStrat;
    const field = dcaAmountField;
    updateStrategyLocal(name, { [field]: amt });
    setDcaAmountStrat(null);
    const ok = await saveStrategyPatch(name, { [field]: amt });
    if (!ok) { toast.error("Failed to update DCA amount"); load(); }
    else toast.success("DCA amount updated");
  };

  const toggleAuto = async (field) => {
    const next = !profile[field];
    setProfile((p) => ({ ...p, [field]: next }));
    const endpoint = field === "auto_trading" ? "auto-trading" : "auto-cashout";
    const data = await apiPost(`/api/user/profile/${endpoint}`, { enabled: next });
    if (!data || !data.success) { setProfile((p) => ({ ...p, [field]: !next })); toast.error("Failed to update"); }
  };

  const saveCashoutTrigger = async (odds) => {
    const data = await apiPost("/api/user/profile/cashout-trigger", { odds });
    if (!data || !data.success) toast.error("Failed to update cash-out trigger");
    else toast.success(`Cash-out trigger set to ${Number(odds).toFixed(1)}`);
  };

  const toggleCashoutMode = async (key) => {
    const next = !profile[key];
    setProfile((p) => ({ ...p, [key]: next }));
    const body = key === "cashout_opp_enabled" ? { opp_enabled: next } : { profit_enabled: next };
    const data = await apiPost("/api/user/profile/cashout-settings", body);
    if (!data || !data.success) { setProfile((p) => ({ ...p, [key]: !next })); toast.error("Failed to update"); }
  };

  const saveCashoutProfitPct = async (pct) => {
    const data = await apiPost("/api/user/profile/cashout-settings", { profit_pct: pct });
    if (!data || !data.success) toast.error("Failed to update profit % trigger");
    else toast.success(`Profit-% trigger set to ${Number(pct).toFixed(0)}%`);
  };

  const saveInfo = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.includes("@")) { toast.error("Valid first name, last name and email required"); return; }
    setSavingInfo(true);
    const data = await apiPost("/api/user/profile/update-info", form);
    setSavingInfo(false);
    if (data && data.success) { toast.success("Profile updated"); setEditOpen(false); load(); }
    else toast.error("Failed to update profile");
  };

  const saveKey = async () => {
    let body;
    if (keyModal === "cloudbet") {
      const v = keyValue.trim();
      if (!v) { toast.error("Please enter your Cloudbet API key"); return; }
      body = { cloudbet_api_key: v };
    } else {
      const token = botTokenValue.trim();
      const chat = keyValue.trim();
      if (!token) { toast.error("Please enter your Telegram Bot Token"); return; }
      if (!chat) { toast.error("Please enter your Telegram Chat ID"); return; }
      body = { telegram_bot_token: token, telegram_chat_id: chat };
    }
    setSavingKey(true);
    const data = await apiPost("/api/user/profile/api-keys", body);
    setSavingKey(false);
    if (data && data.success) {
      if (keyModal === "cloudbet") {
        if (data.cloudbet_verified) {
          toast.success(`Connected! Balance: ${(data.cloudbet_balance ?? 0)} USDT`);
          setKeyModal(null); setKeyValue(""); load();
        } else {
          toast.error(data.cloudbet_error || "Key saved but is NOT authenticating with Cloudbet.");
          load();  // refresh status; keep modal open so they can fix the key
        }
      } else {
        toast.success("Saved"); setKeyModal(null); setKeyValue(""); setBotTokenValue(""); load();
      }
    } else toast.error("Failed to save");
  };

  const testCloudbet = async () => {
    setTestingCb(true);
    const data = await apiPost("/api/user/profile/cloudbet/test", {});
    setTestingCb(false);
    if (data && data.success) toast.success(`Connected! Balance: ${data.balance} ${data.currency || "USDT"}`);
    else toast.error((data && data.error) || "Connection failed");
    load();
  };

  const disconnectKey = async () => {
    const isCloudbet = keyModal === "cloudbet";
    const label = isCloudbet ? "Cloudbet API key" : "Telegram bot";
    if (!window.confirm(`Disconnect your ${label}? ${isCloudbet ? "Automated trading will stop until you reconnect." : "You will stop receiving alerts until you reconnect."}`)) return;
    const body = isCloudbet
      ? { cloudbet_api_key: "" }
      : { telegram_bot_token: "", telegram_chat_id: "" };
    setDisconnecting(true);
    const data = await apiPost("/api/user/profile/api-keys", body);
    setDisconnecting(false);
    if (data && data.success) { toast.success(`${label} disconnected`); setKeyModal(null); setKeyValue(""); setBotTokenValue(""); load(); }
    else toast.error("Failed to disconnect");
  };

  const sendTelegramTest = async () => {
    setTestingTg(true);
    const data = await apiPost("/api/user/profile/telegram/test", {});
    setTestingTg(false);
    if (data && data.success) toast.success("Test alert sent — check your Telegram!");
    else toast.error((data && data.error) || "Could not send test message");
  };

  if (loading) return <div className="p-6"><LoadingBlock /></div>;
  if (!profile) return <div className="p-6 text-[#9ca3af]">Could not load profile.</div>;

  const initials = `${(profile.first_name || "?")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() || "BP";
  const strategies = profile.strategies || {};
  const stratNames = Object.keys(strategies);
  const enabledCount = stratNames.filter((n) => strategies[n].enabled).length;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-4">Settings</h1>
      <div className="flex gap-2 mb-5">
        {[{ k: "profile", l: "Profile", I: User }, { k: "strategies", l: "Strategies", I: Sliders }].map(({ k, l, I }) => (
          <button key={k} onClick={() => setSubTab(k)} data-testid={`settings-tab-${k}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-semibold ${subTab === k ? "bg-[#10b981]/15 border border-[#10b981] text-[#10b981]" : "bg-[#1e293b] text-[#6b7280]"}`}>
            <I size={16} /> {l}
          </button>
        ))}
      </div>

      {subTab === "profile" && (
        <>
          <div className="bg-[#1e293b] rounded-2xl p-5 mb-4 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#10b981] flex items-center justify-center text-black text-xl font-bold">{isAdmin ? "AD" : initials}</div>
            <div className="flex-1">
              <p className="text-lg font-bold text-white">{isAdmin ? "Administrator" : `${profile.first_name} ${profile.last_name}`}</p>
              <p className="text-[13px] text-[#9ca3af]">{isAdmin ? "Full access" : profile.email}</p>
            </div>
            {!isAdmin && (
              <button onClick={() => { setForm({ first_name: profile.first_name, last_name: profile.last_name, email: profile.email, telegram: profile.telegram, discord: profile.discord, x_handle: profile.x_handle }); setEditOpen(true); }}
                data-testid="edit-profile-btn" className="w-10 h-10 rounded-full bg-[#0f172a] flex items-center justify-center text-[#10b981]"><Pencil size={18} /></button>
            )}
          </div>

          {!isAdmin && (profile.telegram || profile.discord || profile.x_handle) && (
            <div className="bg-[#1e293b] rounded-2xl p-4 mb-4 space-y-2">
              {profile.telegram && <div className="flex justify-between"><span className="text-[13px] text-[#9ca3af]">Telegram</span><span className="text-[13px] text-white">{profile.telegram}</span></div>}
              {profile.discord && <div className="flex justify-between"><span className="text-[13px] text-[#9ca3af]">Discord</span><span className="text-[13px] text-white">{profile.discord}</span></div>}
              {profile.x_handle && <div className="flex justify-between"><span className="text-[13px] text-[#9ca3af]">X</span><span className="text-[13px] text-white">{profile.x_handle}</span></div>}
            </div>
          )}

          <p className="text-sm text-[#94a3b8] font-semibold mb-2">API Connections</p>
          {/* Cloudbet */}
          <button onClick={() => { setKeyModal("cloudbet"); setKeyValue(profile.cloudbet_api_key || ""); setRevealKey(false); }} data-testid="manage-cloudbet" className="w-full bg-[#1e293b] rounded-2xl p-4 mb-2.5 flex items-center gap-3 text-left">
            <div className="w-11 h-11 rounded-full bg-[#3b82f6]/15 flex items-center justify-center"><Cloud size={22} color="#3b82f6" /></div>
            <div className="flex-1">
              <p className="text-white font-bold">Cloudbet API</p>
              {!profile.has_cloudbet_key ? (
                <p className="text-[13px] text-[#9ca3af]">Not connected — tap to add</p>
              ) : profile.cloudbet_verified === true ? (
                <p className="text-[13px] text-[#10b981]" data-testid="cloudbet-status">Connected &amp; verified · {profile.cloudbet_api_key_display || "••••"}</p>
              ) : profile.cloudbet_verified === false ? (
                <p className="text-[13px] text-[#ef4444]" data-testid="cloudbet-status">⚠ Key saved but NOT authenticating — trades will not run. Tap to fix.</p>
              ) : (
                <p className="text-[13px] text-[#f59e0b]" data-testid="cloudbet-status">Key saved · tap to verify connection</p>
              )}
            </div>
            {profile.has_cloudbet_key
              ? (profile.cloudbet_verified === false
                  ? <AlertTriangle size={20} color="#ef4444" />
                  : <CheckCircle2 size={20} color={profile.cloudbet_verified === true ? "#10b981" : "#f59e0b"} />)
              : <ChevronRight size={20} color="#6b7280" />}
          </button>
          {/* Telegram */}
          <button onClick={() => { setKeyModal("telegram"); setKeyValue(profile.telegram_chat_id || ""); setBotTokenValue(profile.telegram_bot_token || ""); }} data-testid="manage-telegram" className="w-full bg-[#1e293b] rounded-2xl p-4 mb-2.5 flex items-center gap-3 text-left">
            <div className="w-11 h-11 rounded-full bg-[#0ea5e9]/15 flex items-center justify-center"><Send size={22} color="#0ea5e9" /></div>
            <div className="flex-1"><p className="text-white font-bold">Telegram Alerts</p><p className="text-[13px]" style={{ color: (profile.has_telegram_bot && profile.has_telegram_id) ? "#10b981" : "#9ca3af" }}>{(profile.has_telegram_bot && profile.has_telegram_id) ? `Your bot · ${profile.telegram_chat_id_display || "••••"}` : "Not connected — add your own bot"}</p></div>
            {(profile.has_telegram_bot && profile.has_telegram_id) ? <CheckCircle2 size={20} color="#10b981" /> : <ChevronRight size={20} color="#6b7280" />}
          </button>

          {isAdmin && onOpenAdmin && (
            <button onClick={onOpenAdmin} data-testid="open-admin-panel" className="w-full bg-[#f59e0b]/10 border border-[#f59e0b]/40 rounded-2xl p-4 mb-2.5 flex items-center gap-3 mt-4">
              <ShieldCheck size={22} color="#f59e0b" /><span className="flex-1 text-left text-[#f59e0b] font-bold">Admin Panel</span><ChevronRight size={20} color="#f59e0b" />
            </button>
          )}

          <button onClick={onLogout} data-testid="settings-logout" className="w-full bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-2xl p-4 mt-2 flex items-center justify-center gap-2 text-[#ef4444] font-bold">
            <LogOut size={20} /> Sign Out
          </button>
          <p className="text-center text-[#475569] text-xs mt-6">BREAKPOINT · Automated Tennis Trading</p>
        </>
      )}

      {subTab === "strategies" && (
        <>
          {/* Master switches */}
          <div className="bg-[#1e293b] rounded-2xl p-4 mb-3 flex items-center gap-3 border border-[#10b981]/30">
            <div className="w-11 h-11 rounded-full bg-[#10b981]/15 flex items-center justify-center"><Zap size={22} color="#10b981" /></div>
            <div className="flex-1"><p className="text-white font-bold">Automated Trading</p><p className="text-[12px] text-[#9ca3af]">Bot places bets automatically using enabled strategies</p></div>
            <Switch on={!!profile.auto_trading} onToggle={() => toggleAuto("auto_trading")} testId="auto-trading-switch" />
          </div>
          <div className="bg-[#1e293b] rounded-2xl p-4 mb-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#f59e0b]/15 flex items-center justify-center"><DollarSign size={22} color="#f59e0b" /></div>
            <div className="flex-1"><p className="text-white font-bold">Auto Cash-Out</p><p className="text-[12px] text-[#9ca3af]">Automatically lock in profit when value is high</p></div>
            <Switch on={!!profile.auto_cashout} onToggle={() => toggleAuto("auto_cashout")} testId="auto-cashout-switch" />
          </div>

          {/* Two cash-out triggers — BOTH can run at once; whichever fires first cashes the bet out. */}
          {!!profile.auto_cashout && (
            <div className="space-y-3 mb-4" data-testid="cashout-modes">
              {/* Trigger 1: opponent live odds */}
              <div className="bg-[#1e293b] rounded-2xl p-4" data-testid="cashout-opp-card">
                <div className="flex items-center gap-3 mb-1">
                  <div className="flex-1">
                    <p className="text-white font-bold text-sm">Trigger A — Opponent Odds</p>
                    <p className="text-[12px] text-[#9ca3af]">Cash out when the opponent&apos;s live odds reach your value.</p>
                  </div>
                  <Switch on={!!profile.cashout_opp_enabled} onToggle={() => toggleCashoutMode("cashout_opp_enabled")} testId="cashout-opp-switch" />
                </div>
                {!!profile.cashout_opp_enabled && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] text-[#9ca3af]">Opponent odds ≥</span>
                      <span className="text-[#f59e0b] font-extrabold text-lg" data-testid="cashout-trigger-value">{Number(profile.cashout_trigger_odds ?? 4).toFixed(1)}</span>
                    </div>
                    <input
                      type="range"
                      min={profile.cashout_trigger_min ?? 2.5}
                      max={profile.cashout_trigger_max ?? 10}
                      step="0.5"
                      value={profile.cashout_trigger_odds ?? 4}
                      onChange={(e) => setProfile((p) => ({ ...p, cashout_trigger_odds: parseFloat(e.target.value) }))}
                      onMouseUp={(e) => saveCashoutTrigger(parseFloat(e.target.value))}
                      onTouchEnd={(e) => saveCashoutTrigger(parseFloat(e.target.value))}
                      data-testid="cashout-trigger-slider"
                      className="w-full accent-[#f59e0b] cursor-pointer"
                    />
                    <div className="flex justify-between text-[11px] text-[#6b7280] mt-1"><span>2.5</span><span>10.0</span></div>
                  </div>
                )}
              </div>

              {/* Trigger 2: profit % (current market odds vs backed odds) */}
              <div className="bg-[#1e293b] rounded-2xl p-4" data-testid="cashout-profit-card">
                <div className="flex items-center gap-3 mb-1">
                  <div className="flex-1">
                    <p className="text-white font-bold text-sm">Trigger B — Profit %</p>
                    <p className="text-[12px] text-[#9ca3af]">Cash out when the live cash-out profit % (backed odds vs current market odds) hits your target.</p>
                  </div>
                  <Switch on={!!profile.cashout_profit_enabled} onToggle={() => toggleCashoutMode("cashout_profit_enabled")} testId="cashout-profit-switch" />
                </div>
                {!!profile.cashout_profit_enabled && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] text-[#9ca3af]">Profit % ≥</span>
                      <span className="text-[#10b981] font-extrabold text-lg" data-testid="cashout-profit-value">{Number(profile.cashout_profit_pct ?? 20).toFixed(0)}%</span>
                    </div>
                    <input
                      type="range"
                      min={profile.cashout_profit_min ?? 5}
                      max={profile.cashout_profit_max ?? 500}
                      step="5"
                      value={profile.cashout_profit_pct ?? 20}
                      onChange={(e) => setProfile((p) => ({ ...p, cashout_profit_pct: parseFloat(e.target.value) }))}
                      onMouseUp={(e) => saveCashoutProfitPct(parseFloat(e.target.value))}
                      onTouchEnd={(e) => saveCashoutProfitPct(parseFloat(e.target.value))}
                      data-testid="cashout-profit-slider"
                      className="w-full accent-[#10b981] cursor-pointer"
                    />
                    <div className="flex justify-between text-[11px] text-[#6b7280] mt-1"><span>5%</span><span>500%</span></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {enabledCount > 0 && !profile.auto_trading && (
            <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/40 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="auto-trading-warning">
              <Info size={18} color="#f59e0b" className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[#f59e0b] font-bold text-sm">Automated Trading is OFF</p>
                <p className="text-[13px] text-[#9ca3af] mt-0.5">You have {enabledCount} {enabledCount === 1 ? "strategy" : "strategies"} enabled, but the bot won&apos;t place any bets until you turn on <span className="text-white font-semibold">Automated Trading</span> above.</p>
                <button onClick={() => toggleAuto("auto_trading")} data-testid="enable-auto-trading-btn" className="mt-2.5 inline-flex items-center gap-1.5 bg-[#10b981] hover:bg-[#059669] transition-colors text-white font-bold text-[13px] px-3 py-2 rounded-lg">
                  <Zap size={14} /> Turn on Automated Trading
                </button>
              </div>
            </div>
          )}

          {profile.auto_trading && !profile.has_cloudbet_key && (
            <div className="bg-[#3b82f6]/10 border border-[#3b82f6]/40 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="no-key-warning">
              <Info size={18} color="#3b82f6" className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[#3b82f6] font-bold text-sm">No Cloudbet API key connected</p>
                <p className="text-[13px] text-[#9ca3af] mt-0.5">Automated Trading is on, but the bot can&apos;t place bets without your Cloudbet API key (with Trading permission). Add it under the API tab.</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-2.5">
            <span className="text-sm text-[#94a3b8] font-semibold">Strategies</span>
            <span className="text-[12px] text-[#10b981] font-semibold">{enabledCount}/{stratNames.length} active</span>
          </div>

          {stratNames.map((name) => {
            const s = strategies[name];
            const info = STRATEGY_INFO[name] || { desc: "", risk: "" };
            return (
              <div key={name} className="bg-[#1e293b] rounded-2xl p-4 mb-2.5" style={{ borderLeft: `3px solid ${s.enabled ? "#10b981" : "#334155"}` }} data-testid="strategy-card">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white font-bold text-[15px]">{name.replace(" Strategy", "")}</p>
                      <button onClick={() => setInfoStrat(name)} data-testid="strategy-info-btn" className="text-[#6b7280] hover:text-[#10b981]"><Info size={15} /></button>
                    </div>
                    <p className="text-[12px] text-[#9ca3af] mt-0.5 pr-2">{info.desc}</p>
                  </div>
                  <Switch on={!!s.enabled} onToggle={() => toggleStrategy(name, !s.enabled)} testId="strategy-toggle" />
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#334155]">
                  <span className="text-[12px] text-[#9ca3af]">Trade Amount</span>
                  <button onClick={() => { setAmountStrat(name); setAmountVal(String(s.trade_amount || 5)); }} data-testid="strategy-amount-btn" className="flex items-center gap-1.5 bg-[#0f172a] px-3 py-1.5 rounded-lg">
                    <span className="text-[#10b981] font-bold text-sm">${(s.trade_amount || 0).toFixed(2)}</span><Pencil size={13} color="#6b7280" />
                  </button>
                </div>
                {name === "Match Winner Strategy" && (
                  <div className="mt-3 pt-3 border-t border-[#334155] bg-[#f59e0b]/5 -mx-4 px-4 pb-1 rounded-b-lg" data-testid="mw-dca-card">
                    <div className="flex items-center justify-between">
                      <div className="pr-2">
                        <p className="text-[13px] text-[#fbbf24] font-semibold">DCA — one rebet on a set loss</p>
                        <p className="text-[11px] text-[#9ca3af] mt-0.5">Places one extra bet on your player at the new odds — if they lose set 1, or if they win set 1 then lose set 2 (before set 3).</p>
                      </div>
                      <Switch on={!!s.dca_enabled} onToggle={() => toggleDca(name, !s.dca_enabled)} testId="mw-dca-toggle" />
                    </div>
                    {s.dca_enabled && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-[#9ca3af]">DCA Amount · set 1 lost</span>
                          <button onClick={() => { setDcaAmountStrat(name); setDcaAmountField("dca_amount"); setDcaAmountVal(String(s.dca_amount || s.trade_amount || 1)); }} data-testid="mw-dca-amount-btn" className="flex items-center gap-1.5 bg-[#0f172a] px-3 py-1.5 rounded-lg">
                            <span className="text-[#f59e0b] font-bold text-sm">${(s.dca_amount || s.trade_amount || 0).toFixed(2)}</span><Pencil size={13} color="#6b7280" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-[#9ca3af]">DCA Amount · set 2 lost</span>
                          <button onClick={() => { setDcaAmountStrat(name); setDcaAmountField("dca_amount_2"); setDcaAmountVal(String(s.dca_amount_2 || s.dca_amount || s.trade_amount || 1)); }} data-testid="mw-dca-amount-2-btn" className="flex items-center gap-1.5 bg-[#0f172a] px-3 py-1.5 rounded-lg">
                            <span className="text-[#f59e0b] font-bold text-sm">${(s.dca_amount_2 || s.dca_amount || s.trade_amount || 0).toFixed(2)}</span><Pencil size={13} color="#6b7280" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => setExpandedStrat(expandedStrat === name ? null : name)}
                  data-testid="strategy-logic-toggle"
                  className="w-full flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-[#334155] text-[12px] font-semibold text-[#0ea5e9] hover:text-[#38bdf8]"
                >
                  {expandedStrat === name ? "Hide full logic" : "How it works — view full logic"}
                  <ChevronDown size={14} className={`transition-transform ${expandedStrat === name ? "rotate-180" : ""}`} />
                </button>
                {expandedStrat === name && (
                  <div className="mt-2 bg-[#0f172a] border border-[#334155] rounded-xl p-3.5" data-testid="strategy-logic-detail">
                    <p className="text-[13px] text-[#cbd5e1] whitespace-pre-line leading-6">{info.fullDesc || info.desc || "No details available."}</p>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Edit profile modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Profile" testId="edit-profile-modal">
        {["first_name", "last_name", "email", "telegram", "discord", "x_handle"].map((f) => (
          <div key={f} className="mb-3">
            <label className="text-[13px] text-[#9ca3af] mb-1 block capitalize">{f.replace("_", " ").replace("x handle", "X (Twitter)")}</label>
            <input value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))}
              className="w-full bg-[#0f172a] border border-[#334155] rounded-xl px-4 py-3 text-white outline-none focus:border-[#10b981]" />
          </div>
        ))}
        <button onClick={saveInfo} disabled={savingInfo} data-testid="save-profile-info" className="w-full bg-[#10b981] text-black font-bold py-3.5 rounded-xl mt-2 disabled:opacity-60 flex items-center justify-center gap-2">{savingInfo ? <Spinner size={18} color="#000" /> : (<><Check size={18} /> Save Changes</>)}</button>
      </Modal>

      {/* API key modal */}
      <Modal open={!!keyModal} onClose={() => setKeyModal(null)} title={keyModal === "cloudbet" ? "Cloudbet API Key" : "Connect Your Telegram Bot"} testId="api-key-modal">
        {keyModal === "cloudbet" ? (
          <>
            <p className="text-[13px] text-[#9ca3af] mb-3">Paste your Cloudbet API key (with Read + Trading permissions).</p>
            <a href={CLOUDBET_REFERRAL_URL} target="_blank" rel="noreferrer" data-testid="cloudbet-referral-link"
              className="inline-flex items-center gap-1.5 mb-3 text-[13px] font-semibold text-[#10b981] hover:underline">
              <Gift size={14} /> Don&apos;t have a Cloudbet account? Sign up here
            </a>
            {profile.has_cloudbet_key && (
              <div className="flex items-center gap-2 mb-3 text-[12.5px] text-[#10b981] bg-[#10b981]/10 rounded-lg px-3 py-2" data-testid="cloudbet-connected-hint">
                <CheckCircle2 size={15} /> Connected · your key is shown below (masked) — edit and Save to replace it.
              </div>
            )}
            <div className="relative">
              <textarea value={keyValue} onChange={(e) => setKeyValue(e.target.value)} rows={3} placeholder={profile.has_cloudbet_key ? "Paste a new key to replace…" : "eyJhbGciOiJSUzI1NiIs..."} data-testid="api-key-input"
                style={!revealKey && keyValue ? { WebkitTextSecurity: "disc", textSecurity: "disc" } : undefined}
                className="w-full bg-[#0f172a] border border-[#334155] rounded-xl p-3.5 pr-11 text-white text-sm outline-none focus:border-[#3b82f6] break-all" />
              {keyValue && (
                <button type="button" onClick={() => setRevealKey((r) => !r)} data-testid="cloudbet-reveal-toggle"
                  className="absolute top-2.5 right-2.5 text-[#94a3b8] hover:text-white" title={revealKey ? "Hide key" : "Reveal key"}>
                  {revealKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              )}
            </div>
            {profile.has_cloudbet_key && (
              <button onClick={testCloudbet} disabled={testingCb} data-testid="cloudbet-test-btn"
                className="w-full mt-3 border border-[#3b82f6] text-[#3b82f6] font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                {testingCb ? <Spinner size={16} color="#3b82f6" /> : (<><Cloud size={16} /> Test Connection</>)}
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-[13px] text-[#9ca3af] mb-3">Use your OWN Telegram bot so trade and win/loss alerts are sent privately to you.</p>
            {profile.has_telegram_bot && profile.has_telegram_id && (
              <div className="flex items-center gap-2 mb-3 text-[12.5px] text-[#0ea5e9] bg-[#0ea5e9]/10 rounded-lg px-3 py-2" data-testid="telegram-connected-hint">
                <CheckCircle2 size={15} /> Connected · your bot token & chat ID are shown below — edit and Save to update.
              </div>
            )}
            <div className="bg-[#0f172a] border border-[#334155] rounded-xl p-3.5 mb-4 space-y-2 text-[13px] text-[#cbd5e1] leading-5">
              <p><span className="text-[#0ea5e9] font-bold">1.</span> Open <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-[#0ea5e9] font-semibold hover:underline">@BotFather</a> → send <span className="font-mono bg-[#1e293b] px-1 rounded">/newbot</span> → copy the <b>Bot Token</b>.</p>
              <p><span className="text-[#0ea5e9] font-bold">2.</span> Open <b>your new bot</b> and tap <span className="font-mono bg-[#1e293b] px-1 rounded">Start</span> (so it can message you).</p>
              <p><span className="text-[#0ea5e9] font-bold">3.</span> Open <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-[#0ea5e9] font-semibold hover:underline">@userinfobot</a> to get your numeric <b>Chat ID</b>.</p>
            </div>

            <TelegramGuide />

            <label className="text-[13px] font-semibold text-[#e2e8f0] mb-1.5 block">Bot Token</label>
            <input value={botTokenValue} onChange={(e) => setBotTokenValue(e.target.value)} placeholder="123456789:AAH..." data-testid="telegram-bot-token-input"
              className="w-full bg-[#0f172a] border border-[#334155] rounded-xl p-3.5 text-white text-sm outline-none focus:border-[#0ea5e9] mb-3" />

            <label className="text-[13px] font-semibold text-[#e2e8f0] mb-1.5 block">Chat ID</label>
            <input value={keyValue} onChange={(e) => setKeyValue(e.target.value)} inputMode="numeric" placeholder="123456789" data-testid="api-key-input"
              className="w-full bg-[#0f172a] border border-[#334155] rounded-xl p-3.5 text-white outline-none focus:border-[#0ea5e9]" />

            {profile.has_telegram_bot && profile.has_telegram_id && (
              <button onClick={sendTelegramTest} disabled={testingTg} data-testid="telegram-test-btn"
                className="w-full mt-3 border border-[#0ea5e9] text-[#0ea5e9] font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                {testingTg ? <Spinner size={16} color="#0ea5e9" /> : (<><Send size={16} /> Send Test Message</>)}
              </button>
            )}
          </>
        )}
        <button onClick={saveKey} disabled={savingKey} data-testid="save-api-key" className="w-full bg-[#10b981] text-black font-bold py-3.5 rounded-xl mt-4 disabled:opacity-60">{savingKey ? <Spinner size={18} color="#000" /> : (keyModal === "cloudbet" ? (profile.has_cloudbet_key ? "Update Key" : "Connect") : ((profile.has_telegram_bot && profile.has_telegram_id) ? "Update" : "Connect"))}</button>
        {((keyModal === "cloudbet" && profile.has_cloudbet_key) || (keyModal === "telegram" && profile.has_telegram_bot && profile.has_telegram_id)) && (
          <button onClick={disconnectKey} disabled={disconnecting} data-testid="disconnect-key" className="w-full border border-[#ef4444]/60 text-[#ef4444] font-semibold py-3 rounded-xl mt-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
            {disconnecting ? <Spinner size={16} color="#ef4444" /> : (<><Unlink size={16} /> Disconnect</>)}
          </button>
        )}
      </Modal>

      {/* Strategy info modal */}
      <Modal open={!!infoStrat} onClose={() => setInfoStrat(null)} title={infoStrat ? infoStrat.replace(" Strategy", "") : ""} testId="strategy-info-modal">
        <p className="text-[13px] text-[#cbd5e1] whitespace-pre-line leading-6">{infoStrat ? (STRATEGY_INFO[infoStrat]?.fullDesc || STRATEGY_INFO[infoStrat]?.desc || "No details available.") : ""}</p>
      </Modal>

      {/* Strategy amount modal */}
      <Modal open={!!amountStrat} onClose={() => setAmountStrat(null)} title="Set Trade Amount" maxWidth="max-w-sm" testId="strategy-amount-modal">
        <p className="text-[13px] text-[#9ca3af] mb-3">{amountStrat ? amountStrat.replace(" Strategy", "") : ""}</p>
        <div className="flex items-center bg-[#0f172a] border border-[#334155] rounded-xl px-4 mb-3">
          <span className="text-2xl text-[#10b981] font-bold">$</span>
          <input value={amountVal} onChange={(e) => setAmountVal(e.target.value)} inputMode="decimal" data-testid="amount-input"
            className="flex-1 bg-transparent py-4 text-2xl text-white font-bold text-center outline-none" autoFocus />
          <span className="text-[13px] text-[#6b7280]">USDT</span>
        </div>
        <div className="flex justify-between mb-4">
          {["1", "2", "5", "10", "25"].map((a) => <button key={a} onClick={() => setAmountVal(a)} className="px-3 py-2 bg-[#334155] rounded-md text-white text-[13px] font-semibold">${a}</button>)}
        </div>
        <button onClick={saveAmount} data-testid="save-amount" className="w-full bg-[#10b981] text-black font-bold py-3.5 rounded-xl">Save Amount</button>
      </Modal>

      {/* DCA amount modal (Match Winner) */}
      <Modal open={!!dcaAmountStrat} onClose={() => setDcaAmountStrat(null)} title={dcaAmountField === "dca_amount_2" ? "Set DCA Amount (set 2 lost)" : "Set DCA Amount (set 1 lost)"} maxWidth="max-w-sm" testId="mw-dca-amount-modal">
        <p className="text-[13px] text-[#9ca3af] mb-3">{dcaAmountField === "dca_amount_2" ? "Stake for the rebet placed when your player wins set 1 then loses set 2." : "Stake for the rebet placed when your player loses set 1."}</p>
        <div className="flex items-center bg-[#0f172a] border border-[#334155] rounded-xl px-4 mb-3">
          <span className="text-2xl text-[#f59e0b] font-bold">$</span>
          <input value={dcaAmountVal} onChange={(e) => setDcaAmountVal(e.target.value)} inputMode="decimal" data-testid="mw-dca-amount-input"
            className="flex-1 bg-transparent py-4 text-2xl text-white font-bold text-center outline-none" autoFocus />
          <span className="text-[13px] text-[#6b7280]">USDT</span>
        </div>
        <div className="flex justify-between mb-4">
          {["1", "2", "5", "10", "25"].map((a) => <button key={a} onClick={() => setDcaAmountVal(a)} className="px-3 py-2 bg-[#334155] rounded-md text-white text-[13px] font-semibold">${a}</button>)}
        </div>
        <button onClick={saveDcaAmount} data-testid="mw-save-dca-amount" className="w-full bg-[#f59e0b] text-black font-bold py-3.5 rounded-xl">Save DCA Amount</button>
      </Modal>
    </div>
  );
}
